import React, {
  useState,
  useEffect,
  useCallback
} from 'react';

import {
  FiMenu,
  FiSearch,
  FiCommand
} from 'react-icons/fi';

import SafeIcon from '../../common/SafeIcon';
import AISearchModal from '../search/AISearchModal';
import ClientSwitcher from './ClientSwitcher';

/*
 * =========================================================
 * HEADER
 *
 * Workspace data is provided by ClientSwitcher
 * through the Supabase-backed ClientContext.
 *
 * No hardcoded organization, user, client,
 * notification count, or access permissions.
 * =========================================================
 */

export default function Header({ onMenuClick }) {
  const [isSearchOpen, setIsSearchOpen] =
    useState(false);

  /*
   * -------------------------------------------------------
   * SEARCH HANDLERS
   * -------------------------------------------------------
   */

  const openSearch = useCallback(() => {
    setIsSearchOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    setIsSearchOpen(false);
  }, []);

  /*
   * -------------------------------------------------------
   * KEYBOARD SHORTCUTS
   *
   * Mac: Command + K
   * Windows/Linux: Ctrl + K
   * -------------------------------------------------------
   */

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === 'k'
      ) {
        event.preventDefault();
        openSearch();
      }
    };

    window.addEventListener(
      'keydown',
      handleKeyDown
    );

    return () => {
      window.removeEventListener(
        'keydown',
        handleKeyDown
      );
    };
  }, [openSearch]);

  /*
   * -------------------------------------------------------
   * RENDER
   * -------------------------------------------------------
   */

  return (
    <>
      <header className="sticky top-0 z-20 flex h-[68px] w-full shrink-0 items-center border-b border-slate-200 bg-white/95 px-4 backdrop-blur-sm sm:px-5 lg:px-8">

        <div className="flex w-full min-w-0 items-center justify-between gap-3">

          {/* LEFT: MENU + WORKSPACE */}
          <div className="flex min-w-0 flex-1 items-center gap-3">

            {/* MOBILE SIDEBAR TOGGLE */}
            <button
              type="button"
              onClick={onMenuClick}
              aria-label="Open navigation menu"
              title="Open navigation"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 md:hidden"
            >
              <SafeIcon
                icon={FiMenu}
                className="text-xl"
              />
            </button>

            {/* WORKSPACE SELECTOR */}
            <div className="min-w-0 w-full max-w-[320px] sm:max-w-[340px]">
              <ClientSwitcher />
            </div>

          </div>

          {/* RIGHT: SEARCH */}
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">

            {/* DESKTOP SEARCH */}
            <button
              type="button"
              onClick={openSearch}
              aria-label="Search or ask AI"
              title="Search or ask AI"
              className="hidden h-10 w-[220px] items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3.5 text-left text-slate-500 transition-colors hover:border-slate-300 hover:bg-white lg:flex xl:w-[270px]"
            >
              <span className="flex min-w-0 items-center gap-2.5">
                <SafeIcon
                  icon={FiSearch}
                  className="shrink-0 text-[15px] text-slate-400"
                />

                <span className="truncate text-[13px] font-medium">
                  Search or ask AI...
                </span>
              </span>

              {/* KEYBOARD SHORTCUT */}
              <span className="flex shrink-0 items-center gap-0.5 rounded-md border border-slate-200 bg-white px-1.5 py-1 text-[10px] font-semibold text-slate-400">
                <SafeIcon
                  icon={FiCommand}
                  className="text-[10px]"
                />
                K
              </span>
            </button>

            {/* TABLET / MOBILE SEARCH */}
            <button
              type="button"
              onClick={openSearch}
              aria-label="Search or ask AI"
              title="Search or ask AI"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-900 lg:hidden"
            >
              <SafeIcon
                icon={FiSearch}
                className="text-lg"
              />
            </button>

          </div>
        </div>
      </header>

      {/* EXISTING AI SEARCH MODAL */}
      <AISearchModal
        isOpen={isSearchOpen}
        onClose={closeSearch}
      />
    </>
  );
}